import { Link } from 'react-router-dom'
import {
  ClipboardList,
  CloudSun,
  Flag,
  FlaskConical,
  Home,
  LogOut,
  MapPin,
  PlusCircle,
  RefreshCw,
  Settings,
  Sparkles,
  Store,
  Trophy,
  Users,
} from 'lucide-react'
import CLUB from '../config/club.js'
import { FOURBALL_ONLY, PBCC_PREMIUM } from '../config/siteMode.js'

// The 4-Ball page's four sidebar tabs — shared so the standalone Weather page
// keeps the SAME sidebar (in 4-Ball context) instead of the admin tabs. The
// create tabs navigate to /admin/pbcc-4ball with a ?new= flag; AdminHome reads
// it and fires the matching create flow (works whether you're already on the
// 4-Ball page or coming from Weather). Weather links to its own page.
export function fourBallNavItems(navigate) {
  const items = [
    { key: 'reg',     label: 'New Registration',     action: () => navigate('/admin/pbcc-4ball?new=registration'), Icon: PlusCircle },
    { key: 'qual',    label: 'New Qualifying Event', action: () => navigate('/admin/pbcc-4ball?new=qualifying'),   Icon: Flag },
    { key: 'tourn',   label: 'New Tournament',       action: () => navigate('/admin/pbcc-4ball?new=tournament'),   Icon: Trophy },
    { key: 'calcutta', label: 'Calcutta',            action: () => navigate('/admin/calcutta'),                    Icon: Trophy },
    { key: 'weather', label: 'Weather',              to: '/admin/weather',                                         Icon: CloudSun },
  ]
  // 4-Ball-only site removes Weather.
  return FOURBALL_ONLY ? items.filter((i) => i.key !== 'weather') : items
}

// ── Premium theme tokens (ONE shared source) ────────────────────────────────
// On the TEST site ONLY (PBCC_PREMIUM = pbcc-tourney-test) the shell renders in
// the premium cream/gold palette. The club (THEIRS) and live keep the CLUB accent
// (white in 4-Ball mode / gold in full) — untouched even on a FOURBALL rebuild.
export const PBCC_GREEN = CLUB.primaryColor
export const PBCC_GOLD = PBCC_PREMIUM ? '#C2A24E' : CLUB.accentColor
export const PBCC_GOLD_DARK = PBCC_PREMIUM ? '#9C7B2E' : (CLUB.accentColorDark || '#123D26')
export const PBCC_CREAM = PBCC_PREMIUM ? '#F7F2E6' : '#FFFFFF'

// Shared "← Back to Home" control — top-left, above page content. Returns to
// the landing page (the three-card home). Used by the 4-Ball page and every
// admin-sidebar page so you're never stuck.
export function BackToHome() {
  return (
    <div className="mb-3">
      <Link
        // A signed-in admin must never land on the public marketing page. On the
        // 4-Ball app, "home" IS the 4-Ball dashboard. On the FULL app (yesnetlabs),
        // this page is a SUB-page — home is the Golf Ops hub (/admin, WelcomePage),
        // NOT /admin/pbcc-4ball (which is this same page → the button did nothing).
        to={FOURBALL_ONLY ? '/admin/pbcc-4ball' : (PBCC_PREMIUM ? '/admin' : '/')}
        className="inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm sm:text-sm font-bold uppercase tracking-wide transition"
        style={PBCC_PREMIUM
          ? { color: '#F7F2E6', background: '#123D26', border: '1px solid rgba(194,162,78,0.85)' }
          : FOURBALL_ONLY
            ? { color: '#123D26', background: '#FFFFFF', border: '1px solid #123D26' }
            : { color: '#FFFFFF', background: '#123D26', border: '1px solid #FFFFFF' }}
      >
        <span aria-hidden>←</span> Back to Home
      </Link>
    </div>
  )
}

// Gold accent dividers for the premium (4-Ball) hero — a labeled rule with
// flanking diamonds ("◆ 62ND ANNUAL ◆") and a plain gold-diamond rule.
function GoldLabelDivider({ label }) {
  const dot = { width: 7, height: 7, background: PBCC_GOLD, transform: 'rotate(45deg)', display: 'inline-block', flexShrink: 0 }
  const line = { height: 1, flex: '0 1 90px', background: 'rgba(194,162,78,0.7)' }
  return (
    <div className="flex items-center justify-center gap-3 my-1.5">
      <span style={line} />
      <span style={dot} />
      <span style={{ color: PBCC_GOLD, fontSize: 'clamp(12px, 1.4vw, 16px)', fontWeight: 600, letterSpacing: '0.18em', whiteSpace: 'nowrap' }}>{label.toUpperCase()}</span>
      <span style={dot} />
      <span style={line} />
    </div>
  )
}
function GoldDiamondDivider() {
  const line = { height: 1, flex: '0 1 70px', background: 'rgba(194,162,78,0.55)' }
  return (
    <div className="flex items-center justify-center gap-3 mt-3">
      <span style={line} />
      <span style={{ width: 9, height: 9, border: `1px solid ${PBCC_GOLD}`, transform: 'rotate(45deg)', display: 'inline-block', flexShrink: 0 }} />
      <span style={line} />
    </div>
  )
}

// Slim aerial header used on every admin / inner page. The background is
// CLUB.heroImage (the aerial), CSS-filtered the same way the landing hero
// is, so the rebrand stays consistent across surfaces.
export function PbccHeroHeader({
  variant = 'admin',
  onSignOut,
  onLock,
  onHelp,
}) {
  const heroBg = CLUB.heroImage || CLUB.gpsSatelliteImage || ''

  return (
    <header className="pbcc-hero pbcc-hero-slim relative overflow-hidden">
      {/* Filtered aerial background — same HD treatment as the landing
          hero (contrast + saturate + brightness, scaled slightly to hide
          filter-edge bleed). */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'contrast(1.10) saturate(1.22) brightness(1.04)',
          transform: 'scale(1.02)',
          transformOrigin: 'center',
        }}
      />
      {/* Edge-frame + vertical fade so the title row sits cleanly on the
          photo without muddying the course detail. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: [
            'linear-gradient(to right, rgba(0,0,0,0.30), transparent 18%, transparent 82%, rgba(0,0,0,0.30))',
            'linear-gradient(to bottom, rgba(0,0,0,0.18), rgba(20,46,28,0.92))',
          ].join(', '),
        }}
      />

      <div className={`relative z-10 pl-0 pr-3 sm:pl-1 sm:pr-5 lg:pr-7 ${PBCC_PREMIUM ? 'pt-6 sm:pt-8 pb-8 sm:pb-10' : FOURBALL_ONLY ? 'pt-16 sm:pt-24 pb-16 sm:pb-24' : 'pt-2 sm:pt-2.5 pb-5 sm:pb-6'}`}>
        <div className={`w-full flex items-center gap-4 flex-nowrap ${FOURBALL_ONLY ? 'justify-center relative' : 'justify-between'}`}>
          <div className={`flex items-center gap-3 min-w-0 ${FOURBALL_ONLY ? 'justify-center' : ''}`}>
            {!FOURBALL_ONLY && (
              <img
                src={CLUB.logoLight}
                alt={CLUB.name}
                className="h-[120px] sm:h-[148px] w-auto object-contain shrink-0"
                style={{ filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.45))' }}
              />
            )}
            <div className={FOURBALL_ONLY ? 'text-center' : 'min-w-0 w-fit'}>
              <h1
                className="text-2xl sm:text-3xl md:text-4xl text-white whitespace-nowrap"
                style={{
                  fontFamily: 'Cinzel, serif',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textShadow: '0 2px 8px rgba(0,0,0,0.55)',
                  lineHeight: 1,
                }}>
                {CLUB.name.split(' ').map((word, wi) => {
                  const W = word.toUpperCase()
                  return (
                    <span key={wi}>
                      {wi > 0 ? ' ' : ''}
                      <span style={{ fontSize: '1.35em' }}>{W[0]}</span>
                      {W.slice(1)}
                    </span>
                  )
                })}
              </h1>
              {!FOURBALL_ONLY && <PbccSubtitle />}
              {FOURBALL_ONLY && (
                <div className="text-center text-white mt-2" style={{ fontFamily: 'Cinzel, serif', textShadow: '0 2px 8px rgba(0,0,0,0.55)' }}>
                  {PBCC_PREMIUM ? (
                    <GoldLabelDivider label="62nd Annual" />
                  ) : (
                    <div style={{ fontSize: 'clamp(13px, 1.5vw, 19px)', fontWeight: 600, letterSpacing: '0.06em', lineHeight: 1.4 }}>62nd Annual Pine Bluff Country Club</div>
                  )}
                  <div style={{ fontSize: 'clamp(18px, 2.6vw, 32px)', fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.2, marginTop: 4 }}>Four-Ball Match Play Invitational</div>
                  <div style={{ fontSize: 'clamp(13px, 1.5vw, 19px)', fontWeight: 500, letterSpacing: '0.08em', lineHeight: 1.4, marginTop: 6 }}>July 30th – Aug 2nd, 2026</div>
                  {PBCC_PREMIUM && <GoldDiamondDivider />}
                </div>
              )}
            </div>
          </div>

          <div className={`flex flex-wrap gap-1.5 justify-end shrink-0 ${FOURBALL_ONLY ? 'absolute right-0 top-1/2 -translate-y-1/2' : ''}`}>
            {!FOURBALL_ONLY && (
              <Link to="/map" className="pbcc-header-btn"
                style={PBCC_PREMIUM
                  ? { background: '#123D26', color: '#F7F2E6', border: '1px solid rgba(194,162,78,0.85)' }
                  : undefined}>
                <MapPin size={16} strokeWidth={2} color={PBCC_PREMIUM ? '#C2A24E' : undefined} />
                <span>Course Map</span>
              </Link>
            )}
            <button
              type="button"
              onClick={onSignOut}
              className="pbcc-header-btn"
              style={PBCC_PREMIUM
                ? { background: '#123D26', color: '#F7F2E6', border: '1px solid rgba(194,162,78,0.85)' }
                : FOURBALL_ONLY ? { background: '#FFFFFF', color: '#123D26', border: '1px solid #123D26' } : undefined}>
              <LogOut size={16} strokeWidth={2} color={PBCC_PREMIUM ? '#C2A24E' : undefined} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

function PbccSubtitle() {
  return (
    <div className="flex items-center gap-4 mt-2 w-full">
      <span className="h-px flex-1" style={{ backgroundColor: PBCC_GOLD }} />
      <p
        className="text-sm sm:text-base uppercase font-bold whitespace-nowrap leading-none"
        style={{ color: PBCC_GOLD, letterSpacing: '0.28em' }}>
        Pro Shop Operations
      </p>
      <span className="h-px flex-1" style={{ backgroundColor: PBCC_GOLD }} />
    </div>
  )
}

// Left-rail nav. The active item is solid gold with dark-green text per
// the spec; the System Status card is pinned at the bottom.
export function PbccAdminSidebar({ active = 'dashboard', onCreate, onHelp, items: itemsOverride }) {
  // Pages can pass their own `items` (e.g. the 4-Ball page swaps in its own
  // four tabs). Falls back to the standard admin nav everywhere else.
  const baseItems = [
    { key: 'golfops',     label: 'Golf Ops',          to: '/admin',            Icon: Home },
    { key: 'tournaments', label: 'Tournaments',       to: '/admin/pbcc-4ball', Icon: Trophy },
    { key: 'clubdata',    label: 'Handicap & Courses', to: '/admin/club-data', Icon: Users },
    { key: 'gfeatures',   label: 'G-Features',        to: '/admin/gg-features', Icon: FlaskConical },
    { key: 'gg',          label: 'Pro Shop Suite',    to: '/admin/gg',    Icon: Store },
    { key: 'demos',       label: 'Demos & Practice',  to: '/admin/demos', Icon: Sparkles },
  ]
  // 4-Ball-only site removes Golf Ops (Pro Shop dashboard), the Tournaments tab
  // (its home IS the tournaments page), the Pro Shop Suite, the G-Features hub,
  // and Demos & Practice — leaving only Handicap & Courses.
  const items = itemsOverride || (FOURBALL_ONLY
    ? baseItems.filter((i) => i.key !== 'gg' && i.key !== 'demos' && i.key !== 'golfops' && i.key !== 'gfeatures' && i.key !== 'tournaments')
    : baseItems)

  return (
    <aside className={`pbcc-sidebar ${(PBCC_PREMIUM || !FOURBALL_ONLY) ? 'flex' : 'hidden lg:flex'} flex-col ${(FOURBALL_ONLY || PBCC_PREMIUM) ? 'pbcc-sidebar-4ball' : ''}`}>
      {(FOURBALL_ONLY || PBCC_PREMIUM) && (
        <div className="px-6 pt-5 pb-3 shrink-0">
          <img
            src={PBCC_PREMIUM ? CLUB.logoLight : '/pbcc-pine-green.png'}
            alt={CLUB.name}
            style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
          />
        </div>
      )}
      <nav className="flex-1 pt-0 pb-4 px-2 overflow-y-auto min-h-0">
        {items.map(({ key, label, to, action, Icon }) => {
          const isActive = key === active
          const cls = `pbcc-sidebar-link ${isActive ? 'pbcc-sidebar-link-active' : ''}`
          const inner = (
            <>
              <Icon size={18} strokeWidth={1.9} />
              <span>{label}</span>
            </>
          )
          if (action) {
            return (
              <button key={key} type="button" onClick={action} className={cls}>
                {inner}
              </button>
            )
          }
          return (
            <Link key={key} to={to} className={cls}>
              {inner}
            </Link>
          )
        })}
      </nav>

      <div className="pbcc-status-card m-4 mt-auto">
        <div className="pbcc-status-label text-sm font-bold uppercase tracking-widest mb-2" style={{ color: PBCC_GOLD }}>
          System Status
        </div>
        <div className="flex items-center gap-2 text-sm text-white/90">
          <span className="pbcc-status-dot" />
          <span>All Systems Operational</span>
        </div>
        <div className="flex items-center justify-between mt-3 text-sm text-white/55">
          <span>Last Sync: 12 sec ago</span>
          <RefreshCw size={13} strokeWidth={2} />
        </div>
      </div>
    </aside>
  )
}

// Section heading with a small gold underline above the title — the
// design-system accent that ties pages back to the landing.
export function PbccSectionTitle({ title, subtitle }) {
  return (
    <div>
      <span className="pbcc-section-rule" aria-hidden="true" />
      <h2
        className="text-lg sm:text-xl"
        style={{ fontFamily: 'Cinzel, serif', fontWeight: 600, color: (FOURBALL_ONLY || PBCC_PREMIUM) ? PBCC_GOLD : PBCC_GREEN }}>
        {title}
      </h2>
      {subtitle ? <p className={`text-sm mt-0.5 ${(FOURBALL_ONLY || PBCC_PREMIUM) ? 'text-white' : 'text-gray-500'}`}>{subtitle}</p> : null}
    </div>
  )
}

export function PbccPrimaryButton({ children, className = '', ...props }) {
  return (
    <button
      type="button"
      className={`pbcc-primary-btn ${className}`}
      {...props}>
      {children}
    </button>
  )
}

// Dark-green quick-actions tile rendered at the bottom of the admin
// dashboard. Gold star + uppercase label across the top; six gold-icon
// buttons in a grid below.
export function PbccQuickActions({ onCreate, onHelp, onGpsTest }) {
  const actions = [
    { label: 'Create New Tournament', Icon: PlusCircle, onClick: onCreate },
    { label: 'Import Players',        Icon: Users,      to: '/admin' },
    { label: 'Set Up Flights',        Icon: Flag,       to: '/admin' },
    { label: 'View Live Leaderboard', Icon: Trophy,     to: '/leaderboard' },
    { label: 'Course Map',            Icon: MapPin,     to: '/tv/scrollmap' },
    { label: '🧪 Test Station',       Icon: Settings,   to: '/teststation' },
    // 4-Ball-only site removes the Pro Shop / G-New Features entry.
    { label: 'G-New Features',        Icon: FlaskConical, to: '/admin/gg' },
  ].filter((a) => !FOURBALL_ONLY || a.label !== 'G-New Features')

  return (
    <div className="pbcc-quick-actions">
      <div className="flex items-center gap-2 mb-4" style={{ color: PBCC_GOLD }}>
        <Sparkles size={16} strokeWidth={2} />
        <span className="font-bold uppercase tracking-widest text-sm">Quick Actions</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
        {actions.map(({ label, Icon, onClick, to }) => {
          const inner = (
            <>
              <Icon size={22} strokeWidth={1.8} />
              <span>{label}</span>
            </>
          )
          if (to) {
            return (
              <Link key={label} to={to} className="pbcc-quick-action-btn">
                {inner}
              </Link>
            )
          }
          return (
            <button key={label} type="button" onClick={onClick} className="pbcc-quick-action-btn">
              {inner}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// PbccFeatureBar and PbccRulesCard are retained as-is so any page that
// already imports them (white-label demo / qualifier app) keeps working.
// They are not part of the admin layout per the redesign spec.
export function PbccFeatureBar() {
  const items = [
    { label: 'Live GPS Tracking',    to: '/map',         Icon: MapPin },
    { label: 'Mobile Scoring',       to: '/score',       Icon: ClipboardList },
    { label: 'Live Leaderboards',    to: '/leaderboard', Icon: Trophy },
    { label: 'Tournament Pairings',  to: '/admin',       Icon: Users },
  ]
  return (
    <div className="pbcc-feature-bar">
      {items.map(({ label, to, Icon }, i) => (
        <span key={label} className="inline-flex items-center">
          <Link to={to} className="pbcc-feature-item">
            <Icon size={16} strokeWidth={2} />
            <span>{label}</span>
          </Link>
          {i < items.length - 1 ? <span className="pbcc-feature-divider" aria-hidden="true" /> : null}
        </span>
      ))}
    </div>
  )
}

export function PbccRulesCard({ type = 'play' }) {
  const isPlay = type === 'play'
  const title = isPlay ? 'Rules of Play' : 'Club Rules'
  const rules = isPlay ? CLUB.rulesOfPlay || [] : CLUB.clubRules || []

  return (
    <div className="pbcc-rules-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="pbcc-rules-icon">
          <Flag size={18} strokeWidth={2} />
        </div>
        <h2
          className="text-xl sm:text-2xl"
          style={{ fontFamily: 'Cinzel, serif', fontWeight: 600, color: PBCC_GREEN }}>
          {title}
        </h2>
      </div>
      <ul className="space-y-3">
        {rules.map((rule) => (
          <li key={rule} className="flex gap-3 text-sm leading-relaxed text-gray-700">
            <span style={{ color: PBCC_GOLD, fontWeight: 800 }}>✓</span>
            <span>{rule}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 text-sm font-bold uppercase tracking-widest" style={{ color: PBCC_GOLD }}>
        View Full Rules &gt;
      </div>
    </div>
  )
}
